import { useEffect, useState } from "react";
import { api, errText, setToken } from "../lib/api";
import { Avatar, CreatorName, Empty, Loading, formatNum } from "./common";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { Users, Shield, Swords, MapPin, Bell, LogOut, X, ShieldCheck, Link2 } from "lucide-react";

export const PHONE_SCREENS = ["feed", "discover", "reels", "profile", "messages", "notifications", "music", "tv", "city", "crew", "settings", "events", "career"];

// Lua gateway puts a 120 s single-use code in the URL (#t=<32 hex>[.<screen>]); legacy JWTs still pass through.
export function parsePhoneHash() {
  const m = (window.location.hash || "").match(/(?:token|t)=([^&]+)/);
  if (!m) return {};
  const raw = decodeURIComponent(m[1]);
  const cm = raw.match(/^([a-f0-9]{32})(?:\.([a-z]+))?$/);
  if (cm) return { code: cm[1], screen: PHONE_SCREENS.includes(cm[2]) ? cm[2] : null };
  return { token: raw };
}

export async function exchangePhoneCode(code) {
  const { data } = await api.post("/virtucity/phone/exchange", { code });
  setToken(data.token);
  return data.token;
}

const CAT_LABEL = { social: ["Follows, likes, comments & mentions", "New followers, likes, comments, reposts, mentions and tips"], messages: ["Direct messages", "New DMs"],
  live: ["Live streams", "When creators you follow go live"], crew: ["Vcrew", "Invitations, approvals and promotions"],
  city: ["VirtuCity", "Property, jobs, vehicles and city announcements"], business: ["Business & events", "Bookings, event reminders and sales"] };

export function PhoneSettings({ user, onSignin, onLogout }) {
  const [prefs, setPrefs] = useState(null);
  useEffect(() => { if (user) api.get("/phone/notification-prefs").then(({ data }) => setPrefs(data.prefs)).catch(() => setPrefs({})); }, [user]);
  if (!user) return <div className="p-6"><Empty icon={Bell} title="Sign in to manage settings" action={<button onClick={onSignin} className="btn-gold rounded-full px-4 py-2 text-sm">Sign in</button>} /></div>;
  const toggle = async (c, v) => {
    setPrefs((p) => ({ ...p, [c]: v }));
    try { const { data } = await api.put("/phone/notification-prefs", { prefs: { [c]: v } }); setPrefs(data.prefs); } catch (e) { toast.error(errText(e)); }
  };
  const vc = user.virtucity || {};
  return (
    <div className="p-3 space-y-3" data-testid="sl-settings">
      <div className="surface rounded-xl p-4 flex items-center gap-3">
        <Avatar src={user.avatar_url} name={user.display_name} size={44} />
        <div className="min-w-0 flex-1"><div className="font-semibold truncate">{user.display_name}</div><div className="text-xs text-muted-foreground">@{user.username}</div></div>
      </div>
      <div className="surface rounded-xl p-4 space-y-2 text-sm" data-testid="sl-settings-links">
        <div className="font-semibold mb-1">VirtuCity account links</div>
        <LinkRow ok={!!vc.discord_id} label="Discord" testid="sl-link-discord" />
        <LinkRow ok={!!vc.fivem_license} label="Rockstar license" testid="sl-link-license" />
        <LinkRow ok={!!(vc.verified && vc.discord_id && vc.fivem_license)} label="Verified Citizen" testid="sl-link-verified" />
        {!(vc.verified && vc.discord_id && vc.fivem_license) && <p className="text-[11px] text-muted-foreground">Finish linking on virtustars.com → VirtuCity to unlock city and Vcrew features.</p>}
      </div>
      <div className="surface rounded-xl p-4" data-testid="sl-notif-prefs">
        <div className="font-semibold text-sm mb-2 flex items-center gap-2"><Bell size={15} className="text-eblue" />In-game phone notifications</div>
        {!prefs ? <Loading /> : Object.keys(CAT_LABEL).map((c) => (
          <div key={c} className="flex items-center gap-3 py-2 border-t border-white/5 first:border-0">
            <div className="flex-1 min-w-0"><div className="text-sm">{CAT_LABEL[c][0]}</div><div className="text-[11px] text-muted-foreground">{CAT_LABEL[c][1]}</div></div>
            <Switch checked={prefs[c] !== false} onCheckedChange={(v) => toggle(c, v)} data-testid={`sl-notif-pref-${c}`} />
          </div>
        ))}
      </div>
      <button onClick={onLogout} className="w-full surface rounded-xl p-4 flex items-center justify-center gap-2 text-rose-300" data-testid="sl-logout"><LogOut size={16} />Sign out of this phone</button>
    </div>
  );
}

function LinkRow({ ok, label, testid }) {
  return <div className="flex items-center gap-2" data-testid={testid}>{ok ? <ShieldCheck size={15} className="text-emerald-400" /> : <Link2 size={15} className="text-muted-foreground" />}<span className="flex-1">{label}</span><span className={`text-xs ${ok ? "text-emerald-400" : "text-muted-foreground"}`}>{ok ? "Linked" : "Not linked"}</span></div>;
}

export function CrewScreen({ user, onSignin }) {
  const [d, setD] = useState(null);
  const load = () => api.get("/vstreets/my-crews").then(({ data }) => setD(data)).catch((e) => setD({ error: errText(e) }));
  useEffect(() => { if (user) load(); }, [user]);
  if (!user) return <div className="p-6"><Empty icon={Swords} title="Sign in to see your Vcrew" action={<button onClick={onSignin} className="btn-gold rounded-full px-4 py-2 text-sm">Sign in</button>} /></div>;
  if (!d) return <Loading />;
  if (d.error) return <div className="p-6"><Empty icon={Swords} title="Vcrew unavailable" hint={d.error} /></div>;
  if (!d.crews.length) return <div className="p-6"><Empty icon={Swords} title="No approved Vcrew yet" hint="Join or create a Vcrew on virtustars.com → VSTREETS." /></div>;
  const pick = async (id) => { try { const { data } = await api.put("/vstreets/active-crew", { crew_id: id }); setD((x) => ({ ...x, ...data, requires_selection: false })); toast.success("Active Vcrew updated. It syncs to the HUD within 30 s."); } catch (e) { toast.error(errText(e)); } };
  return (
    <div className="p-3 space-y-3" data-testid="sl-crew">
      {d.requires_selection && <div className="rounded-xl p-3 text-xs bg-amber-500/10 border border-amber-500/30 text-amber-200" data-testid="sl-crew-needs-selection">You're in more than one Vcrew. Pick the one to use in VirtuCity.</div>}
      {d.crews.map((c) => (
        <div key={c.crew_id} className={`surface rounded-xl p-4 ${c.active ? "border border-gold/50" : ""}`} data-testid={`sl-crew-${c.crew_id}`}>
          <div className="flex items-center gap-2">
            <Shield size={18} className={c.active ? "text-gold" : "text-eblue"} />
            <div className="font-display font-bold flex-1 truncate">{c.name}</div>
            {c.active ? <span className="text-[10px] uppercase tracking-widest text-gold">Active</span> :
              <button onClick={() => pick(c.crew_id)} className="text-xs btn-gold rounded-full px-3 py-1" data-testid={`sl-crew-use-${c.crew_id}`}>Use in city</button>}
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3 text-center">
            <div className="surface-2 rounded-lg p-2"><div className="font-bold capitalize" data-testid={`sl-crew-rank-${c.crew_id}`}>{c.rank}</div><div className="text-[10px] text-muted-foreground">Rank</div></div>
            <div className="surface-2 rounded-lg p-2"><div className="font-bold">{c.npc_count}/{c.npc_limit}</div><div className="text-[10px] text-muted-foreground">NPC slots</div></div>
            <div className="surface-2 rounded-lg p-2"><div className="font-bold">{(c.territories || []).length}</div><div className="text-[10px] text-muted-foreground">Blocks</div></div>
          </div>
          {(c.territories || []).length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">{c.territories.map((t) => <span key={t.id} className="text-[11px] surface-2 rounded-full px-2 py-0.5 flex items-center gap-1"><MapPin size={10} />{t.name || t.id}</span>)}</div>}
        </div>
      ))}
    </div>
  );
}

export function PeopleSheet({ userId, kind, onClose }) {
  const [list, setList] = useState(null);
  useEffect(() => { api.get(`/users/${userId}/${kind}`).then(({ data }) => setList(data)).catch(() => setList([])); }, [userId, kind]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end mx-auto max-w-[512px]" onClick={onClose}>
      <div className="w-full max-h-[80%] glass rounded-t-2xl p-4 overflow-y-auto animate-rise" onClick={(e) => e.stopPropagation()} data-testid={`sl-people-${kind}`}>
        <div className="flex items-center justify-between mb-3"><div className="font-display font-bold capitalize flex items-center gap-2"><Users size={16} />{kind}</div><button onClick={onClose} data-testid="sl-people-close"><X size={20} /></button></div>
        {!list ? <Loading /> : list.length === 0 ? <Empty icon={Users} title={`No ${kind} yet`} /> : list.map((u) => (
          <a key={u.id} href={`/${u.username}`} className="flex items-center gap-3 py-2 border-t border-white/5 first:border-0" data-testid={`sl-person-${u.id}`}>
            <Avatar src={u.avatar_url} name={u.display_name} size={38} />
            <div className="min-w-0 flex-1"><div className="text-sm font-semibold truncate"><CreatorName user={u} /></div><div className="text-[11px] text-muted-foreground">@{u.username} · {formatNum(u.follower_count || 0)} followers</div></div>
          </a>
        ))}
      </div>
    </div>
  );
}
